/**
 * Read Cache Primitives
 *
 * localStorage-backed cache for DB-first reads (stale-while-revalidate): paint
 * the cached value instantly, fetch the DB (source of truth), overwrite.
 * Generalizes the user_contacts cache-paint template in ConnectionStateContext.
 * See docs/core/data-and-state-guide.md § DB-First Architecture.
 *
 * Consumer contract (useCachedRead + direct compositions):
 * - The cache is overwritten on every successful refresh and NEVER written on a
 *   failed one. Fetchers must THROW on failure — a benign default ({}/[]/null)
 *   returned from a failed fetch would overwrite good cached data.
 * - A release that changes a cached value's shape must rename the key so old
 *   data reads as a miss.
 */

/**
 * Read a cached value.
 * Absent key and corrupt JSON both return a miss — the DB refresh is
 * authoritative either way.
 *
 * @param {string} cacheKey
 * @returns {{hit: boolean, value: any}}
 */
export const readCache = (cacheKey) => {
  const raw = localStorage.getItem(cacheKey);
  if (raw === null) return { hit: false, value: null };
  try {
    return { hit: true, value: JSON.parse(raw) };
  } catch {
    return { hit: false, value: null };
  }
};

/**
 * Write a value to the cache (overwrite-on-refresh).
 * Quota/serialization failures are logged and non-fatal — the app runs uncached.
 *
 * @param {string} cacheKey
 * @param {any} value
 */
export const writeCache = (cacheKey, value) => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(value));
  } catch (err) {
    console.error('[READ_CACHE] write failed:', cacheKey, err?.message);
  }
};

/**
 * Remove a cached value.
 *
 * @param {string} cacheKey
 */
export const clearCache = (cacheKey) => {
  localStorage.removeItem(cacheKey);
};
